import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import ContactName from "@/components/access-contacts/contact-name-left-section/ContactName";
import Contact from "@/components/access-contacts/contact-right-section/Contact";
import Interaction from "@/components/access-contacts/interaction-middle-section/Interaction";
import { useContactDetails } from "@/hooks/useContactDetails";
import { useCustomFields } from "@/hooks/useCustomFields";
import { isValidUUID } from "@/utils/contactUtils";

const AccessContacts: React.FC = () => {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const isValidContactId = Boolean(contactId && isValidUUID(contactId));

  // Campos personalizados em paralelo com o contato (nível conta)
  const { fields: customFieldDefs, loading: customFieldsLoading } = useCustomFields();
  const { contact, loading, error, updateContact, addInteraction, updateInteraction, deleteInteraction, refresh } =
    useContactDetails(isValidContactId ? contactId : undefined);

  if (contactId && !isValidContactId) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-4">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4 dark:bg-red-500/20">
          <svg className="w-8 h-8 text-[#EF4444]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-[#0F172A] dark:text-[#FAFAFA]">ID de contato inválido</h3>
        <p className="text-[#64748B] text-center max-w-md dark:text-[#71717A]">
          O ID fornecido não é válido. Por favor, verifique o link e tente novamente.
        </p>
        <button
          onClick={() => navigate('/crm')}
          className="flex items-center gap-2 px-4 py-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl transition-colors duration-200 mt-4"
        >
          <ArrowLeft size={16} />
          Voltar para CRM
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-[#E5E7EB] border-t-[#2563EB] rounded-full animate-spin dark:border-[#3F3F46] dark:border-t-[#60A5FA]"></div>
          <p className="text-[#64748B] dark:text-[#71717A]">Carregando contato...</p>
        </div>
      </div>
    );
  }

  if (error || !contact) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-4">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4 dark:bg-red-500/20">
          <svg className="w-8 h-8 text-[#EF4444]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-[#0F172A] dark:text-[#FAFAFA]">Contato não encontrado</h3>
        <p className="text-[#64748B] text-center max-w-md dark:text-[#71717A]">
          {error || 'Não foi possível encontrar este contato. Ele pode ter sido removido ou você não tem permissão para acessá-lo.'}
        </p>
        <button
          onClick={() => navigate('/crm')}
          className="flex items-center gap-2 px-4 py-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl transition-colors duration-200 mt-4"
        >
          <ArrowLeft size={16} />
          Voltar para CRM
        </button>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-4">
      <button
        onClick={() => navigate('/crm')}
        className="flex items-center gap-2 text-[#64748B] hover:text-[#2563EB] transition-colors duration-200 dark:text-[#A1A1AA] dark:hover:text-[#60A5FA]"
      >
        <ArrowLeft size={16} />
        Voltar para CRM
      </button>

      <div className="w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[1fr_2fr_1fr] gap-4">
        <ContactName
          contact={contact}
          onUpdate={updateContact}
          customFieldDefs={customFieldDefs}
          customFieldsLoading={customFieldsLoading}
        />
        <Interaction
          contact={contact}
          interactions={contact.interactions}
          onAddInteraction={addInteraction}
          onUpdateInteraction={updateInteraction}
          onDeleteInteraction={deleteInteraction}
        />
        <Contact contact={contact} onUpdate={updateContact} onRefresh={refresh} />
      </div>
    </div>
  );
};

export default AccessContacts;
